import {useState, type FormEvent} from 'react'
import {useNavigate} from 'react-router-dom'
import {Eye, EyeOff, Lock, Mail, Loader2, AlertCircle} from 'lucide-react'
import {sha256} from 'crypto-hash'

// Ajuste os imports abaixo conforme seu projeto (mantive o padrão do código antigo)
import * as API from '../../../api'
import {getUserByToken} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'

interface LoginFormProps {
  // Opcional: se você quiser manter compatibilidade, pode passar algo,
  // mas por padrão vamos redirecionar via router.
  onForgotPassword?: () => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function LoginForm({onForgotPassword}: LoginFormProps) {
  const navigate = useNavigate()
  const {saveAuth, setCurrentUser} = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState({email: false, password: false})

  const emailTrimmed = email.trim()

  const emailError =
    touched.email && !emailTrimmed
      ? 'O preenchimento do e-mail é necessário'
      : touched.email && !EMAIL_REGEX.test(emailTrimmed)
        ? 'Formato de e-mail incorreto'
        : ''

  const passwordError =
    touched.password && !password
      ? 'O preenchimento da senha é necessário'
      : touched.password && password.length > 0 && password.length < 6
        ? 'Mínimo de 6 caracteres'
        : ''

  const isValid = EMAIL_REGEX.test(emailTrimmed) && password.length >= 6

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setTouched({email: true, password: true})

    if (!isValid || loading) return

    setLoading(true)
    setError('')

    try {
      // Mantém a mesma lógica do código antigo: senha vai em SHA-256
      const passwordHash = await sha256(password)

      const auth = await API.login(emailTrimmed, passwordHash)

      // Mesmo tratamento do legado
      if (!auth?.success && auth?.error === 'MAINTENANCE') {
        setError('Servidor em manutenção. Tente novamente mais tarde.')
        setLoading(false)
        return
      }

      if (!auth?.success || !auth?.data?.api_token) {
        // fallback seguro (quando o backend retorna success=false, ou estrutura inesperada)
        saveAuth(undefined)
        setError('Dados de acesso incorretos')
        setLoading(false)
        return
      }

      // Salva token e busca usuário (igual ao código antigo)
      saveAuth(auth.data)
      const {data: user} = await getUserByToken(auth.data.api_token)
      setCurrentUser(user)

      // Se você quiser redirecionar após login, descomente e ajuste a rota:
      // navigate('/')

    } catch {
      saveAuth(undefined)
      setError('Dados de acesso incorretos')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPasswordClick = () => {
    if (typeof onForgotPassword === 'function') {
      onForgotPassword()
      return
    }
    navigate('../forgot-password') // /auth/login -> /auth/forgot-password
  }

  return (
    <div
      className="w-full max-w-[520px] animate-card-entry translate-y-4 text-[1.05rem] leading-relaxed font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] box-border"
      role="main"
    >
      <div className="bg-white/[0.92] backdrop-blur-[24px] border border-white/70 rounded-[22px] px-8 py-10 sm:px-11 sm:py-12 shadow-[0_6px_10px_-2px_rgba(0,50,120,0.05),0_28px_55px_-10px_rgba(0,50,120,0.10),0_0_0_1px_rgba(0,80,180,0.03)]">
        <div className="flex flex-col items-center mb-9">
          <img
            src={toAbsoluteUrl('/media/logos/logo-wide.png')}
            alt="Compra Agora Interno - Logo"
            className="h-14 w-auto mb-3 object-contain"
          />
          <p className="text-sm sm:text-[0.95rem] text-slate-500 text-center">
            Entre com suas credenciais para continuar
          </p>
        </div>

        {error && (
          <div
            className="flex items-center gap-2.5 px-4 py-3.5 mb-6 text-[0.95rem] text-red-700 bg-red-50 border border-red-200 rounded-xl leading-snug"
            role="alert"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div className="flex flex-col">
            <label
              htmlFor="email"
              className="block text-[0.95rem] font-semibold text-slate-700 mb-2 leading-snug"
            >
              E-mail
            </label>

            <div
              className={`login-wrapper ${emailError ? 'wrapper-error' : ''} relative flex items-center bg-slate-50/80 rounded-xl transition-all duration-200 ${
                emailError
                  ? 'border-[1.5px] border-red-500'
                  : 'border-[1.5px] border-slate-200 hover:border-slate-300 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100/70'
              }`}
            >
              <Mail
                size={20}
                className="login-icon absolute left-4 text-slate-400 pointer-events-none transition-colors duration-200"
              />
              <input
                id="email"
                type="email"
                placeholder="Digite aqui seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((p) => ({...p, email: true}))}
                autoComplete="email"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'email-error' : undefined}
                className="login-input w-full py-4 pl-12 pr-4 text-[1rem] text-slate-800 bg-transparent border-none outline-none leading-normal placeholder:text-slate-400"
              />
            </div>

            {emailError && (
              <p
                id="email-error"
                className="text-[0.9rem] text-red-600 mt-2 pl-0.5 leading-snug"
                role="alert"
              >
                {emailError}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-[0.95rem] font-semibold text-slate-700 mb-2 leading-snug"
              >
                Senha
              </label>

              <button
                type="button"
                onClick={handleForgotPasswordClick}
                className="text-[0.9rem] text-blue-600 hover:text-blue-700 font-semibold transition-colors"
              >
                Esqueceu sua senha?
              </button>
            </div>

            <div
              className={`login-wrapper ${passwordError ? 'wrapper-error' : ''} relative flex items-center bg-slate-50/80 rounded-xl transition-all duration-200 ${
                passwordError
                  ? 'border-[1.5px] border-red-500'
                  : 'border-[1.5px] border-slate-200 hover:border-slate-300 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100/70'
              }`}
            >
              <Lock
                size={20}
                className="login-icon absolute left-4 text-slate-400 pointer-events-none transition-colors duration-200"
              />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((p) => ({...p, password: true}))}
                autoComplete="current-password"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'password-error' : undefined}
                className="login-input w-full py-4 pl-12 pr-12 text-[1rem] text-slate-800 bg-transparent border-none outline-none leading-normal placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="login-pass-toggle absolute right-3 flex items-center justify-center p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-white/70 bg-transparent border-none cursor-pointer transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100/80"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {passwordError && (
              <p
                id="password-error"
                className="text-[0.9rem] text-red-600 mt-2 pl-0.5 leading-snug"
                role="alert"
              >
                {passwordError}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !isValid}
            className={`login-submit w-full py-4 text-[1.05rem] font-semibold text-white bg-gradient-to-br from-[#0066ff] to-[#0055dd] border-none rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden leading-normal shadow-[0_10px_20px_-10px_rgba(0,102,255,0.65)] hover:shadow-[0_16px_28px_-14px_rgba(0,102,255,0.75)] hover:-translate-y-[1px] active:translate-y-0 ${
              loading || !isValid
                ? 'opacity-70 cursor-not-allowed hover:translate-y-0'
                : ''
            }`}
            aria-busy={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={20} className="animate-spin" />
                Aguarde, por favor...
              </span>
            ) : (
              'Acessar'
            )}
          </button>
        </form>

        <div className="mt-7 pt-6 border-t border-slate-100">
          <p className="text-[0.9rem] text-slate-400 text-center font-light">
            Acesso restrito a usuários autorizados
          </p>
        </div>
      </div>
    </div>
  )
}
